import { useMemo, type ReactNode } from 'react';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import { MultiSelectNoScrollCapture as MultiSelect } from '__components/Inputs/MultiSelect';
import type {
  AutomationMetaData,
  GenericOption,
  VariableCompatibility,
  VariableOption,
} from 'pages/AutomationEngine/types';
import { useTranslation } from 'react-i18next';
import type { VariableNameAndType } from '../VariableWizard/types';
import {
  useAutomationsDispatch,
  useAutomationsSelector,
} from 'pages/AutomationEngine/store/react';
import useModal from '__components/Modals/useModal';
import { TopMenuButton } from '__components/Kizen/Menu';
import { ArrayAggregationModeOptions } from '../VariableWizard/constants';
import { actions } from 'pages/AutomationEngine/store';
import { AUTOMATION_STEPS } from 'pages/AutomationEngine/steps';
import { ACTIONS_ON_FAILURE } from '../VariableWizard/utilities';
import { AddVariableModal } from './AddVariableModal';
import { getObjectKeys } from 'pages/AutomationEngine/ts-helpers';
import { getMetaData } from 'pages/AutomationEngine/store/selectors';

const getAllowedVariableDataTypes = (metaData: AutomationMetaData) => {
  const meta = metaData.variables?.variable_from_variable;
  const compatibility: VariableCompatibility = {};

  const dataTypes = getObjectKeys(meta);

  dataTypes.forEach((dataType) => {
    compatibility[dataType] = {
      single: meta?.[dataType]?.single ? {} : undefined,
      array: meta?.[dataType]?.array ? {} : undefined,
    };
  });
  return compatibility;
};

export type VariableOptionGroup = {
  label: string;
  options: Array<VariableOption>;
};

type VariableSelectWithAddProps<T extends boolean> = {
  isMulti?: T;
  value?: (T extends true ? GenericOption[] : GenericOption) | null;
  onChange: (value: T extends true ? VariableOption[] : VariableOption) => void;
  options: Array<VariableOption | VariableOptionGroup>;
  allowedDataTypes?: VariableCompatibility;
  allowedSubtype?: GenericOption;
  showCompatibilityNotice?: boolean;
  label?: string | null;
  labelInfo?: string;
  labelInfoPlacement?: string;
  labelInfoMaxWidth?: number;
  placeholder?: string;
  margin?: boolean | string;
  error?: boolean;
  validate?:
    | { message: string | string[] | null; showMessage?: boolean }
    | undefined;
  menuInline?: boolean;
  menuLeftButton?: ReactNode;
  noOptionsMessage?: string;
  portal?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  inModal?: boolean;
  'data-qa'?: string;
  dataQa?: string;
  'data-field-type'?: string;
  'data-field-id'?: string;
  onMenuOpen?: () => void;
  includeGroupPlaceholder?: boolean;
};

export const VariableSelectWithAdd = <T extends boolean = false>({
  isMulti = false as T,
  value,
  onChange,
  allowedDataTypes,
  allowedSubtype,
  showCompatibilityNotice = true,
  label,
  placeholder,
  ...rest
}: VariableSelectWithAddProps<T>) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();

  const metaData: AutomationMetaData = useAutomationsSelector(getMetaData);

  const defaultAllowedDataTypes = useMemo(
    () => getAllowedVariableDataTypes(metaData),
    [metaData]
  );

  const [modalProps, buttonProps] = useModal({
    handleSubmit: (variable: VariableNameAndType, order: number) => {
      const id = `new.${AUTOMATION_STEPS.initialize_variable.type}.${Date.now()}`;
      const payload = {
        step: {
          id,
          type: AUTOMATION_STEPS.initialize_variable.type,
          details: {
            id,
            type: AUTOMATION_STEPS.initialize_variable.type,
            variable,
            sources: [],
            sourcesMask: [],
            arrayAggregationMode: ArrayAggregationModeOptions.ADD_ALL,
            deduplicateArray: false,
          },
          order,
          prefix: AUTOMATION_STEPS.initialize_variable.type,
          actionOnFailure: ACTIONS_ON_FAILURE.NOTIFY_PAUSE,
          considerEmptyAnError: false,
          userDescription: '',
          description: `${variable.name} = ${variable.isArray ? '[]' : '<i>empty</i>'}`,
        },
        silent: true,
      };

      dispatch(actions.createVariableDefinition(payload));

      const option: VariableOption = {
        value: variable.name,
        label: variable.name,
        meta: variable,
      };

      onChange(
        (isMulti
          ? [...(value as VariableOption[]), option]
          : option) as T extends true ? VariableOption[] : VariableOption
      );
    },
  });

  const Component = isMulti ? MultiSelect : Select;

  return (
    <>
      <Component
        value={value}
        onChange={onChange}
        placeholder={placeholder ?? t('Choose Variable')}
        label={label || label === null ? label : t('Variable')}
        menuTopButton={
          <TopMenuButton {...buttonProps}>{t('Add Variable')}</TopMenuButton>
        }
        {...rest}
      />
      {modalProps.show ? (
        <AddVariableModal
          {...modalProps}
          allowedDataTypes={allowedDataTypes ?? defaultAllowedDataTypes}
          allowedSubtype={allowedSubtype}
          showCompatibilityNotice={showCompatibilityNotice}
        />
      ) : null}
    </>
  );
};
